import React, { useState, useCallback, useEffect, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';

import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { AnalyticsSource, ViewTracker } from '@atlassian/jira-analytics-web-react';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import iframeRedirect from '@atlassian/jira-common-navigation/src/iframe-redirect';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { TASK_FAIL, TASK_SUCCESS } from '@atlassian/jira-experience-tracker/src/common/constants';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { FormattedMessage, useIntlV2 as useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import {
	GIC_LIFECYCLE_EVENTS,
	useGICLifeCycleEvents,
} from '@atlassian/jira-issue-create-extensibility';
import { AsyncDevOpsSummaryContainer } from '@atlassian/jira-issue-dev-ops-summary/src/async';
import { ItemLineCardGroup } from '@atlassian/jira-issue-item-line-card';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics';
import { APPLINK_REQ_AUTH } from '@atlassian/jira-issue-view-common-constants';
import type { LinkedIssue } from '@atlassian/jira-issue-view-common-types/src/children-issues-type';
import {
	isLeftClickOnly,
	isMiddleClick,
	isCmdOrCtrlClick,
} from '@atlassian/jira-issue-view-common-utils';
import { renderInlineMessage } from '@atlassian/jira-issue-view-common-views/src/issue-line-card';
import {
	SectionHeading,
	SectionHeadingIcons,
	SectionHeadingTitle,
} from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view';
import {
	useSmartCardProductType,
	useSmartCardIsAIEnabled,
} from '@atlassian/jira-linking-platform-utils';
import {
	COMMAND_PALETTE_CREATE_LINKED_ISSUE,
	ISSUE_VIEW_CREATE_LINKED_ISSUE,
} from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants';
import { ISSUE_LINK_SEARCH } from '@atlassian/jira-platform-field-config';
import {
	MODAL,
	fireUIAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey, useApplication, useEdition } from '@atlassian/jira-project-context-service';
import { toIssueKey, type IssueId, type IssueKey, toHref } from '@atlassian/jira-shared-types';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { isCustomMessageForLinkingWithoutPermissionsEnabled } from '../../feature-flags';
import IssueLinksAddView from './add';
import AddIssueLinkButton from './add/add-button';
import transformIssueLink from './add/create-linked-issue-button/transformer';
import { IssueLinkLineCard } from './issue-links-line-card';
import messages from './messages';
import type { LinkedIssueType, IssueLink, Props } from './types';
import { getUserHasPermissionToLink } from './utils';

const loadedIssueLinksCount = (issueLinks: Record<string, unknown[]> = {}) =>
	issueLinks
		? Object.keys(issueLinks).reduce(
				(totalLinksCount: number, typeName: string) =>
					issueLinks[typeName].length + totalLinksCount,
				0,
			)
		: 0;

const getIssueIdsForDevSummaries = (issueLinks: IssueLink): IssueId[] =>
	Object.values(issueLinks).flatMap((issueLinksGroup) =>
		issueLinksGroup
			// The `@atlassian/jira-issue-dev-ops-summary` package used for fetching/rendering
			// dev summary info doesn't support multiple cloud IDs. So ignore remote links here.
			.filter((issueLink) => !issueLink.isRemote)
			.map((issueLink) => issueLink.linkedIssue.id),
	);

const ConfirmationModal = ({
	confirmActionText,
	contentMessage,
	externalId,
	headerMessage,
	onConfirm,
	onCancel,
}: {
	confirmActionText: string;
	contentMessage: string;
	externalId: string;
	headerMessage: string;
	onConfirm: (e: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	onCancel: (e: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
}) => (
	<>
		<ViewTracker />
		<ShortcutScope>
			<ModalDialog width="small" onClose={onCancel} testId={externalId}>
				<ModalHeader>
					<ModalTitle appearance="warning">{headerMessage}</ModalTitle>
				</ModalHeader>
				<ModalBody>{contentMessage}</ModalBody>
				<ModalFooter>
					<Button
						appearance="warning"
						onClick={onConfirm}
						/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
						testId="issue.views.issue-base.content.issue-links.confirm-button"
						interactionName="auto-generated.issue.views.issue-base.content.issue-links.confirm-button.button"
					>
						{confirmActionText}
					</Button>
					<Button
						appearance="subtle"
						onClick={onCancel}
						/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
						testId="issue.views.issue-base.content.issue-links.cancel-button"
						interactionName="auto-generated.issue.views.issue-base.content.issue-links.cancel-button.button1"
					>
						<FormattedMessage
							id="issue.views.issue-base.content.issue-links.cancel-button-text"
							defaultMessage="Cancel"
						/>
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	</>
);

export const TrackedConfirmationModal = AnalyticsSource(
	'removeLinkedIssue',
	MODAL,
)(ConfirmationModal);

export const IssueLinksViewOld = ({
	issueLinks = null,
	issueLinkTypes = [],
	isIssueViewComplete = false,
	onIssueLinkClicked = noop,
	onIssueLinkRendered = noop,
	onIssueLinkAddClick = noop,
	onIssueLinkAddClosed = noop,
	onRemoveIssueLinkInitiated = noop,
	onMountCreatePermissionFetching,
	displayAddIssueLinks,
	createAnalyticsEvent,
	onAuthenticateApplink,
	linkActions,
	deleteLinkedIssue,
	canAddIssueLinks,
	hasLinks,
	retryLinkedIssue,
	cancelLinkedIssue,
	projectType,
	baseUrl,
	issueKey,
	onLinkedIssueCreated,
	issueId,
	onEditIssue,
}: Props) => {
	const intl = useIntl();

	const [state, setState] = useState({
		hasDeleteModal: false,
		groupId: null,
		issueKey: null,
		globalId: undefined,
	});

	useEffect(() => {
		if (hasLinks) {
			// no need to dispatch fetch request if no links
			// @ts-expect-error - TS2345 - Argument of type 'IssueLink | null' is not assignable to parameter of type '{} | undefined'.
			onIssueLinkRendered(loadedIssueLinksCount(issueLinks));
		}
		onMountCreatePermissionFetching();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { subscribe } = useGICLifeCycleEvents();

	useEffect(
		() =>
			subscribe(GIC_LIFECYCLE_EVENTS.ISSUE_CREATE_SUCCESS, async (payload) => {
				if (
					payload.callbackPayload?.id !== ISSUE_VIEW_CREATE_LINKED_ISSUE &&
					payload.callbackPayload?.id !== COMMAND_PALETTE_CREATE_LINKED_ISSUE
				)
					return;

				const { createdIssueDetails } = payload.createdIssueData;
				const { sourceIssueId, baseUrl: callbackPayloadBaseUrl } = payload.callbackPayload.data;

				if (issueId !== sourceIssueId) return;

				const analyticsEvent = createAnalyticsEvent({
					action: 'created',
				});
				if (ff('corex-operandi-issue-view-additional-logging_l6hj8')) {
					onLinkedIssueCreated?.(
						transformIssueLink(
							createdIssueDetails,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							sourceIssueId as string,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							callbackPayloadBaseUrl as string,
						),
						createAnalyticsEvent({
							action: 'linkedToProject',
						}),
						analyticsEvent,
					);
				} else {
					onLinkedIssueCreated?.(
						transformIssueLink(
							createdIssueDetails,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							sourceIssueId as string,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							callbackPayloadBaseUrl as string,
						),
						analyticsEvent,
					);
				}
			}),
		[subscribe, onLinkedIssueCreated, createAnalyticsEvent, issueId],
	);

	const handleOnIssueClickNew = useCallback(
		(
			// @ts-expect-error - TS2322 - Type '{}' is not assignable to type 'LinkedIssue'.
			{
				id,
				issueKey: lIssueKey,
				issueLink,
				remoteIssueLinkError,
				projectType: targetProjectType = null,
				isRemote,
			}: LinkedIssue = {},
			event: MouseEvent,
		) => {
			if (lIssueKey) {
				const isIssueNeedAuth = remoteIssueLinkError?.error === APPLINK_REQ_AUTH;

				if (onIssueLinkClicked) {
					// @ts-expect-error - TS2345 - Argument of type 'IssueLink | null' is not assignable to parameter of type '{} | undefined'.
					onIssueLinkClicked(id, targetProjectType, loadedIssueLinksCount(issueLinks));
				}
				if (isLeftClickOnly(event)) {
					if (isIssueNeedAuth) {
						const repairLinkHref = remoteIssueLinkError?.repairLink?.href;
						if (repairLinkHref && onAuthenticateApplink) {
							onAuthenticateApplink(toHref(repairLinkHref));
						}
					} else {
						issueLink && iframeRedirect(issueLink, event.metaKey, linkActions.push);
					}
				}
				if (isMiddleClick(event) || isCmdOrCtrlClick(event)) {
					!isIssueNeedAuth && issueLink && window.open(issueLink, '_blank');
				}

				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'card',
				});
				fireUIAnalytics(analyticsEvent, 'issueLinkedCard', {
					clickedIssueId: id,
					// @ts-expect-error - TS2345 - Argument of type 'IssueLink | null' is not assignable to parameter of type '{} | undefined'.
					linkedIssuesCount: loadedIssueLinksCount(issueLinks),
					issueNeedsAuthentication: isIssueNeedAuth,
					isRemote,
				});
			}
		},
		[createAnalyticsEvent, issueLinks, linkActions.push, onAuthenticateApplink, onIssueLinkClicked],
	);

	const getLinkByGroupAndIssueKey = useCallback(
		(groupId: string | null, lIssueKey: IssueKey | null, globalId?: string) =>
			((issueLinks && groupId && issueLinks[groupId]) || []).find(
				(issueLink) => issueLink.linkedIssueKey === lIssueKey && issueLink.globalId === globalId,
			),
		[issueLinks],
	);

	const flattenedIssueLinks = useCallback(
		() =>
			issueLinks
				? // eslint-disable-next-line @typescript-eslint/no-explicit-any
					Object.keys(issueLinks).reduce<Array<any>>( // eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						(acc: LinkedIssueType[], curr: string) => [...acc, ...issueLinks[curr]],
						[],
					)
				: [],
		[issueLinks],
	);

	const modalClosedStateChanges = useCallback(() => {
		setState((oldState) => ({
			...oldState,
			groupId: null,
			issueKey: null,
			hasDeleteModal: false,
			globalId: undefined,
		}));
	}, []);

	const handleOnDeleteConfirm = useCallback(
		(groupId: string | null, lIssueKey: IssueKey | null, globalId?: string) => {
			const linkToDelete = getLinkByGroupAndIssueKey(groupId, lIssueKey, globalId);
			if (linkToDelete) {
				const deleteLinkId = linkToDelete.id;
				deleteLinkedIssue({ linkId: deleteLinkId, globalId }, undefined, undefined);

				const indexOfDeletedLink = flattenedIssueLinks().findIndex(
					(link) => link.id === deleteLinkId && link.globalId === globalId,
				);
				onRemoveIssueLinkInitiated(indexOfDeletedLink);
			}
			modalClosedStateChanges();
		},
		[
			deleteLinkedIssue,
			flattenedIssueLinks,
			getLinkByGroupAndIssueKey,
			modalClosedStateChanges,
			onRemoveIssueLinkInitiated,
		],
	);

	const handleOnDeleteCancel = useCallback(() => {
		modalClosedStateChanges();
	}, [modalClosedStateChanges]);

	const getRemovalConfirmationText = useCallback(
		(lIssueKey: IssueKey | null) => ({
			confirmationHeader: intl.formatMessage(messages.removeIssueLinkHeader, {
				issueKey: lIssueKey,
			}),
			confirmationContent: intl.formatMessage(messages.removeIssueLinkContent),
		}),
		[intl],
	);

	const handleOnDeleteAction = useCallback(
		(groupId: string, lIssueKey: IssueKey, globalId?: string) => {
			// @ts-expect-error - TS2345 - Argument of type '(oldState: { hasDeleteModal: boolean; groupId: null; issueKey: null; globalId: undefined; }) => { groupId: string; issueKey: string; globalId: string | undefined; hasDeleteModal: true; }' is not assignable to parameter of type 'SetStateAction<{ hasDeleteModal: boolean; groupId: null; issueKey: null; globalId: undefined; }>'.
			setState((oldState) => ({
				...oldState,
				groupId,
				issueKey: lIssueKey,
				globalId,
				hasDeleteModal: true,
			}));

			const linkToDelete = getLinkByGroupAndIssueKey(groupId, lIssueKey, globalId);
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			fireUIAnalytics(analyticsEvent, 'linkedIssueCardDeleteButton', {
				issueLinkToDeleteId: get(linkToDelete, ['linkedIssue', 'id']),
				isRemote: !isEmpty(globalId),
			});
		},
		[createAnalyticsEvent, getLinkByGroupAndIssueKey],
	);

	const renderDeleteConfirmationModal = useCallback(() => {
		const { groupId, issueKey: sIssueKey, globalId } = state;
		const texts = getRemovalConfirmationText(sIssueKey);
		const { confirmationHeader, confirmationContent } = texts;

		return (
			<TrackedConfirmationModal
				onCancel={handleOnDeleteCancel}
				onConfirm={() => handleOnDeleteConfirm(groupId, sIssueKey, globalId)}
				headerMessage={confirmationHeader}
				contentMessage={confirmationContent}
				confirmActionText={intl.formatMessage(messages.removeIssueLinkButton)}
				externalId="issue.issueLinks.delete"
			/>
		);
	}, [getRemovalConfirmationText, handleOnDeleteCancel, handleOnDeleteConfirm, intl, state]);

	const renderIssueLinkInlineMessage = useCallback(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(id: string, isRemote: boolean, userHasPermissionToLink = true as Boolean) => {
			let inlineContentMessage = messages.issueLinkFailureContent;

			if (isCustomMessageForLinkingWithoutPermissionsEnabled()) {
				inlineContentMessage = userHasPermissionToLink
					? messages.issueLinkFailureContent
					: messages.issueLinkNoPermissionFailureContent;
			}
			return renderInlineMessage(
				intl,
				intl.formatMessage(messages.issueLinkFailureHeader),
				intl.formatMessage(inlineContentMessage),
				(event, analyticsEvent) =>
					ff('corex-operandi-issue-view-additional-logging_l6hj8')
						? retryLinkedIssue(
								id,
								isRemote,
								createAnalyticsEvent({ action: 'succeeded', isRemote }),
								createAnalyticsEvent({ action: 'linkedToProject' }),
								noop,
								noop,
								analyticsEvent.update({ isRemote }),
							)
						: retryLinkedIssue(
								id,
								isRemote,
								createAnalyticsEvent({ action: 'succeeded', isRemote }),
								analyticsEvent.update({ isRemote }),
								noop,
								noop,
							),
				() => cancelLinkedIssue(id, isRemote),
				isCustomMessageForLinkingWithoutPermissionsEnabled() && userHasPermissionToLink,
			);
		},
		[cancelLinkedIssue, createAnalyticsEvent, intl, retryLinkedIssue],
	);

	const renderLineCardGroup = useCallback(
		(issues: LinkedIssue[], name: string) => (
			<SmartCardProvider>
				<ItemLineCardGroupWrapper>
					<ItemLineCardGroup
						items={issues}
						groupId={name}
						isInteractive={isIssueViewComplete}
						onClick={handleOnIssueClickNew}
					>
						{({ isHovering, isActive, isFocused, item: issue }) => {
							const handleClick = (event: MouseEvent) => handleOnIssueClickNew({ ...issue }, event);

							return (
								<IssueLinkLineCard
									isHovering={isHovering}
									isActive={isActive}
									isFocused={isFocused}
									issue={issue}
									groupId={name}
									isInteractive={isIssueViewComplete}
									onDelete={(groupId, lIssueKey, globalId) =>
										handleOnDeleteAction(groupId, toIssueKey(lIssueKey), globalId)
									}
									onClick={handleClick}
									canAddIssueLinks={canAddIssueLinks}
									projectType={projectType}
									onEditIssue={onEditIssue}
								/>
							);
						}}
					</ItemLineCardGroup>
				</ItemLineCardGroupWrapper>
			</SmartCardProvider>
		),
		[
			canAddIssueLinks,
			handleOnDeleteAction,
			handleOnIssueClickNew,
			isIssueViewComplete,
			onEditIssue,
			projectType,
		],
	);

	const renderGroup = useCallback(
		(name: string, links: LinkedIssueType[]) => {
			const issues: LinkedIssue[] = links.map((link) => {
				const errorInlineMessageOld = renderIssueLinkInlineMessage(link.id, !!link.isRemote);

				const errorInlineMessageNew = renderIssueLinkInlineMessage(
					link.id,
					!!link.isRemote,
					getUserHasPermissionToLink(link.error),
				);

				const errorInlineMessage = isCustomMessageForLinkingWithoutPermissionsEnabled()
					? errorInlineMessageNew
					: errorInlineMessageOld;

				return {
					...link.linkedIssue,
					projectType: link.linkedIssue.projectType || null,
					isLoading: link.isLoading || false,
					hasError: link.hasError,
					errorInlineMessage: link.hasError ? errorInlineMessage : undefined,
					id: link.globalId || link.linkedIssue.id,
					isRemote: link.isRemote,
					globalId: link.isRemote ? link.globalId : undefined,
					remoteServerHostname: link.isRemote ? link.remoteServerHostname : undefined,
					remoteIssueLinkError: link.isRemote ? link.remoteIssueLinkError : undefined,
				};
			});

			return (
				<GroupContainer
					key={name}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="issue.views.issue-base.content.issue-links.group-container"
				>
					<RelationshipHeading
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						data-testid="issue.issue-view.views.issue-base.content.issue-links.issue-links-view.relationship-heading"
					>
						{name}
					</RelationshipHeading>
					{renderLineCardGroup(issues, name)}
				</GroupContainer>
			);
		},
		[renderIssueLinkInlineMessage, renderLineCardGroup],
	);

	const renderHeading = useCallback(
		() => (
			<StyledSectionHeading>
				<Label htmlFor={ISSUE_LINK_SEARCH}>
					<SectionHeadingTitle>
						<FormattedMessage id="issue.issue-links-heading" defaultMessage="Linked issues" />
					</SectionHeadingTitle>
				</Label>
				<SectionHeadingIcons>
					{canAddIssueLinks && hasLinks && <AddIssueLinkButton onClick={onIssueLinkAddClick} />}
				</SectionHeadingIcons>
			</StyledSectionHeading>
		),
		[canAddIssueLinks, hasLinks, onIssueLinkAddClick],
	);

	const renderExistingLinks = useCallback(() => {
		const renderedGroups = (
			<div>
				{issueLinks &&
					Object.keys(issueLinks)
						.sort()
						.map((group) => renderGroup(group, issueLinks[group]))}
			</div>
		);
		return isDevopsFeatureDisabledInFedRamp() ? (
			renderedGroups
		) : (
			<AsyncDevOpsSummaryContainer
				issueIds={getIssueIdsForDevSummaries(issueLinks ?? {})}
				location="issue_view_linked_issues_list"
			>
				{renderedGroups}
			</AsyncDevOpsSummaryContainer>
		);
	}, [issueLinks, renderGroup]);

	const renderAddIssueLink = useCallback(
		() => (
			<AddContainer>
				<div>
					<IssueLinksAddView
						issueLinkTypes={issueLinkTypes}
						baseUrl={baseUrl}
						issueKey={issueKey}
						onClose={onIssueLinkAddClosed}
						issueLinks={issueLinks}
					/>
				</div>
			</AddContainer>
		),
		[baseUrl, issueKey, issueLinkTypes, issueLinks, onIssueLinkAddClosed],
	);

	const displayIssueLinks = hasLinks && !isEmpty(issueLinks);

	return (
		<UFOSegment name="issue-view-linked-issues">
			<JSErrorBoundary
				id="issue.issue-view.activity.linked-issues"
				packageName="jiraLinkedIssues"
				teamName="bento"
				fallback="unmount"
			>
				<ContextualAnalyticsData
					sourceName="linkedIssues"
					sourceType={SCREEN}
					attributes={{ location: 'jiraLinkedIssues' }}
				>
					{displayIssueLinks || displayAddIssueLinks ? renderHeading() : null}
					{displayIssueLinks ? renderExistingLinks() : null}
					{displayAddIssueLinks ? renderAddIssueLink() : null}
					{state.hasDeleteModal ? renderDeleteConfirmationModal() : null}
				</ContextualAnalyticsData>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

export const IssueLinksView = ({
	issueLinks = null,
	issueLinkTypes = [],
	isIssueViewComplete = false,
	onIssueLinkClicked = noop,
	onIssueLinkRendered = noop,
	onIssueLinkAddClick = noop,
	onIssueLinkAddClosed = noop,
	onRemoveIssueLinkInitiated = noop,
	onMountCreatePermissionFetching,
	displayAddIssueLinks,
	createAnalyticsEvent,
	onAuthenticateApplink,
	linkActions,
	deleteLinkedIssue,
	canAddIssueLinks,
	hasLinks,
	retryLinkedIssue,
	cancelLinkedIssue,
	projectType,
	baseUrl,
	issueKey,
	onLinkedIssueCreated,
	issueId,
	onEditIssue,
}: Props) => {
	const intl = useIntl();

	const [state, setState] = useState({
		hasDeleteModal: false,
		groupId: null,
		issueKey: null,
		globalId: undefined,
	});

	const linkedIssueKey: string = useIssueKey();
	const projectKey = useProjectKey(linkedIssueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);
	const smartCardProviderProduct = useSmartCardProductType();
	const isAIFeatureEnabled = useSmartCardIsAIEnabled();

	useEffect(() => {
		if (hasLinks) {
			// no need to dispatch fetch request if no links
			// @ts-expect-error - TS2345 - Argument of type 'IssueLink | null' is not assignable to parameter of type '{} | undefined'.
			onIssueLinkRendered(loadedIssueLinksCount(issueLinks));
		}
		onMountCreatePermissionFetching();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { subscribe } = useGICLifeCycleEvents();

	useEffect(
		() =>
			subscribe(GIC_LIFECYCLE_EVENTS.ISSUE_CREATE_SUCCESS, async (payload) => {
				if (
					payload.callbackPayload?.id !== ISSUE_VIEW_CREATE_LINKED_ISSUE &&
					payload.callbackPayload?.id !== COMMAND_PALETTE_CREATE_LINKED_ISSUE
				)
					return;

				const { createdIssueDetails } = payload.createdIssueData;
				const { sourceIssueId, baseUrl: callbackPayloadBaseUrl } = payload.callbackPayload.data;

				if (issueId !== sourceIssueId) return;

				const analyticsEvent = createAnalyticsEvent({
					action: 'created',
				});
				if (ff('corex-operandi-issue-view-additional-logging_l6hj8')) {
					onLinkedIssueCreated?.(
						transformIssueLink(
							createdIssueDetails,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							sourceIssueId as string,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							callbackPayloadBaseUrl as string,
						),
						createAnalyticsEvent({
							action: 'linkedToProject',
						}),
						analyticsEvent,
					);
				} else {
					onLinkedIssueCreated?.(
						transformIssueLink(
							createdIssueDetails,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							sourceIssueId as string,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							callbackPayloadBaseUrl as string,
						),
						analyticsEvent,
					);
				}
			}),
		[subscribe, onLinkedIssueCreated, createAnalyticsEvent, issueId],
	);

	const handleOnIssueClickNew = useCallback(
		(
			// @ts-expect-error - TS2322 - Type '{}' is not assignable to type 'LinkedIssue'.
			{
				id,
				issueKey: lIssueKey,
				issueLink,
				remoteIssueLinkError,
				projectType: targetProjectType = null,
				isRemote,
			}: LinkedIssue = {},
			event: MouseEvent,
		) => {
			if (ff('ken-524-fix-middle-click-issue-for-linked-issues_x1hgl') && isMiddleClick(event))
				return;

			if (lIssueKey) {
				const isIssueNeedAuth = remoteIssueLinkError?.error === APPLINK_REQ_AUTH;

				if (onIssueLinkClicked) {
					// @ts-expect-error - TS2345 - Argument of type 'IssueLink | null' is not assignable to parameter of type '{} | undefined'.
					onIssueLinkClicked(id, targetProjectType, loadedIssueLinksCount(issueLinks));
				}
				if (isLeftClickOnly(event)) {
					if (isIssueNeedAuth) {
						const repairLinkHref = remoteIssueLinkError?.repairLink?.href;
						if (repairLinkHref && onAuthenticateApplink) {
							onAuthenticateApplink(toHref(repairLinkHref));
						}
					} else {
						issueLink && iframeRedirect(issueLink, event.metaKey, linkActions.push);
					}
				}
				if (isMiddleClick(event) || isCmdOrCtrlClick(event)) {
					!isIssueNeedAuth && issueLink && window.open(issueLink, '_blank');
				}

				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'card',
				});
				fireUIAnalytics(analyticsEvent, 'issueLinkedCard', {
					clickedIssueId: id,
					// @ts-expect-error - TS2345 - Argument of type 'IssueLink | null' is not assignable to parameter of type '{} | undefined'.
					linkedIssuesCount: loadedIssueLinksCount(issueLinks),
					issueNeedsAuthentication: isIssueNeedAuth,
					isRemote,
				});
			}
		},
		[createAnalyticsEvent, issueLinks, linkActions.push, onAuthenticateApplink, onIssueLinkClicked],
	);

	const getLinkByGroupAndIssueKey = useCallback(
		(groupId: string | null, lIssueKey: IssueKey | null, globalId?: string) =>
			((issueLinks && groupId && issueLinks[groupId]) || []).find(
				(issueLink) => issueLink.linkedIssueKey === lIssueKey && issueLink.globalId === globalId,
			),
		[issueLinks],
	);

	const flattenedIssueLinks = useCallback(
		() =>
			issueLinks
				? // eslint-disable-next-line @typescript-eslint/no-explicit-any
					Object.keys(issueLinks).reduce<Array<any>>( // eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						(acc: LinkedIssueType[], curr: string) => [...acc, ...issueLinks[curr]],
						[],
					)
				: [],
		[issueLinks],
	);

	const modalClosedStateChanges = useCallback(() => {
		setState((oldState) => ({
			...oldState,
			groupId: null,
			issueKey: null,
			hasDeleteModal: false,
			globalId: undefined,
		}));
	}, []);

	const handleOnDeleteConfirm = useCallback(
		(groupId: string | null, lIssueKey: IssueKey | null, globalId?: string) => {
			const linkToDelete = getLinkByGroupAndIssueKey(groupId, lIssueKey, globalId);
			if (linkToDelete) {
				const deleteLinkId = linkToDelete.id;
				deleteLinkedIssue({ linkId: deleteLinkId, globalId }, application, edition);
				const indexOfDeletedLink = flattenedIssueLinks().findIndex(
					(link) => link.id === deleteLinkId && link.globalId === globalId,
				);
				onRemoveIssueLinkInitiated(indexOfDeletedLink);
			}
			modalClosedStateChanges();
		},
		[
			application,
			deleteLinkedIssue,
			edition,
			flattenedIssueLinks,
			getLinkByGroupAndIssueKey,
			modalClosedStateChanges,
			onRemoveIssueLinkInitiated,
		],
	);

	const handleOnDeleteCancel = useCallback(() => {
		modalClosedStateChanges();
	}, [modalClosedStateChanges]);

	const getRemovalConfirmationText = useCallback(
		(lIssueKey: IssueKey | null) => ({
			confirmationHeader: intl.formatMessage(messages.removeIssueLinkHeader, {
				issueKey: lIssueKey,
			}),
			confirmationContent: intl.formatMessage(messages.removeIssueLinkContent),
		}),
		[intl],
	);

	const handleOnDeleteAction = useCallback(
		(groupId: string, lIssueKey: IssueKey, globalId?: string) => {
			// @ts-expect-error - TS2345 - Argument of type '(oldState: { hasDeleteModal: boolean; groupId: null; issueKey: null; globalId: undefined; }) => { groupId: string; issueKey: string; globalId: string | undefined; hasDeleteModal: true; }' is not assignable to parameter of type 'SetStateAction<{ hasDeleteModal: boolean; groupId: null; issueKey: null; globalId: undefined; }>'.
			setState((oldState) => ({
				...oldState,
				groupId,
				issueKey: lIssueKey,
				globalId,
				hasDeleteModal: true,
			}));

			const linkToDelete = getLinkByGroupAndIssueKey(groupId, lIssueKey, globalId);
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});

			fireUIAnalytics(analyticsEvent, 'linkedIssueCardDeleteButton', {
				issueLinkToDeleteId: get(linkToDelete, ['linkedIssue', 'id']),
				isRemote: !isEmpty(globalId),
			});
		},
		[createAnalyticsEvent, getLinkByGroupAndIssueKey],
	);

	const renderDeleteConfirmationModal = useCallback(() => {
		const { groupId, issueKey: sIssueKey, globalId } = state;
		const texts = getRemovalConfirmationText(sIssueKey);
		const { confirmationHeader, confirmationContent } = texts;

		return (
			<TrackedConfirmationModal
				onCancel={handleOnDeleteCancel}
				onConfirm={() => handleOnDeleteConfirm(groupId, sIssueKey, globalId)}
				headerMessage={confirmationHeader}
				contentMessage={confirmationContent}
				confirmActionText={intl.formatMessage(messages.removeIssueLinkButton)}
				externalId="issue.issueLinks.delete"
			/>
		);
	}, [getRemovalConfirmationText, handleOnDeleteCancel, handleOnDeleteConfirm, intl, state]);

	const onSuccess = useCallback(() => {
		sendExperienceAnalytics({
			wasExperienceSuccesful: true,
			action: TASK_SUCCESS,
			analyticsSource: 'jiraLinkedIssues',
			experience: 'addLinkedIssue',
			application: application ?? null,
			edition: edition ?? null,
		});
	}, [application, edition]);

	const onError = useCallback(() => {
		sendExperienceAnalytics({
			wasExperienceSuccesful: false,
			action: TASK_FAIL,
			analyticsSource: 'jiraLinkedIssues',
			experience: 'addLinkedIssue',
			application: application ?? null,
			edition: edition ?? null,
		});
	}, [application, edition]);

	const renderIssueLinkInlineMessage = useCallback(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(id: string, isRemote: boolean, userHasPermissionToLink = true as Boolean) => {
			let inlineContentMessage = messages.issueLinkFailureContent;

			if (isCustomMessageForLinkingWithoutPermissionsEnabled()) {
				inlineContentMessage = userHasPermissionToLink
					? messages.issueLinkFailureContent
					: messages.issueLinkNoPermissionFailureContent;
			}
			return renderInlineMessage(
				intl,
				intl.formatMessage(messages.issueLinkFailureHeader),
				intl.formatMessage(inlineContentMessage),
				(event, analyticsEvent) =>
					ff('corex-operandi-issue-view-additional-logging_l6hj8')
						? retryLinkedIssue(
								id,
								isRemote,
								createAnalyticsEvent({ action: 'succeeded', isRemote }),
								createAnalyticsEvent({ action: 'linkedToProject' }),
								onSuccess,
								onError,
								analyticsEvent.update({ isRemote }),
							)
						: retryLinkedIssue(
								id,
								isRemote,
								createAnalyticsEvent({ action: 'succeeded', isRemote }),
								analyticsEvent.update({ isRemote }),
								onSuccess,
								onError,
							),
				() => cancelLinkedIssue(id, isRemote),
				isCustomMessageForLinkingWithoutPermissionsEnabled() && userHasPermissionToLink,
			);
		},
		[cancelLinkedIssue, createAnalyticsEvent, intl, onError, onSuccess, retryLinkedIssue],
	);

	const renderLineCardGroup = useCallback(
		(issues: LinkedIssue[], name: string) => (
			<SmartCardProvider
				{...(ff('platform.linking-platform.smart-card.hover-card-ai-summaries') && {
					product: smartCardProviderProduct,
					isAdminHubAIEnabled: isAIFeatureEnabled,
				})}
			>
				<ItemLineCardGroupWrapper>
					<ItemLineCardGroup
						items={issues}
						groupId={name}
						isInteractive={isIssueViewComplete}
						onClick={handleOnIssueClickNew}
					>
						{({ isHovering, isActive, isFocused, item: issue }) => {
							const handleClick = (event: MouseEvent) => handleOnIssueClickNew({ ...issue }, event);

							return (
								<IssueLinkLineCard
									isHovering={isHovering}
									isActive={isActive}
									isFocused={isFocused}
									issue={issue}
									groupId={name}
									isInteractive={isIssueViewComplete}
									onDelete={(groupId, lIssueKey, globalId) =>
										handleOnDeleteAction(groupId, toIssueKey(lIssueKey), globalId)
									}
									onClick={handleClick}
									canAddIssueLinks={canAddIssueLinks}
									projectType={projectType}
									onEditIssue={onEditIssue}
								/>
							);
						}}
					</ItemLineCardGroup>
				</ItemLineCardGroupWrapper>
			</SmartCardProvider>
		),
		[
			canAddIssueLinks,
			handleOnDeleteAction,
			handleOnIssueClickNew,
			isIssueViewComplete,
			onEditIssue,
			projectType,
			smartCardProviderProduct,
			isAIFeatureEnabled,
		],
	);

	const renderGroup = useCallback(
		(name: string, links: LinkedIssueType[]) => {
			const issues: LinkedIssue[] = links.map((link) => {
				const errorInlineMessageOld = renderIssueLinkInlineMessage(link.id, !!link.isRemote);

				const errorInlineMessageNew = renderIssueLinkInlineMessage(
					link.id,
					!!link.isRemote,
					getUserHasPermissionToLink(link.error),
				);

				const errorInlineMessage = isCustomMessageForLinkingWithoutPermissionsEnabled()
					? errorInlineMessageNew
					: errorInlineMessageOld;

				return {
					...link.linkedIssue,
					projectType: link.linkedIssue.projectType || null,
					isLoading: link.isLoading || false,
					hasError: link.hasError,
					errorInlineMessage: link.hasError ? errorInlineMessage : undefined,
					id: link.globalId || link.linkedIssue.id,
					isRemote: link.isRemote,
					globalId: link.isRemote ? link.globalId : undefined,
					remoteServerHostname: link.isRemote ? link.remoteServerHostname : undefined,
					remoteIssueLinkError: link.isRemote ? link.remoteIssueLinkError : undefined,
				};
			});

			return (
				<GroupContainer
					key={name}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="issue.views.issue-base.content.issue-links.group-container"
				>
					<RelationshipHeading
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						data-testid="issue.issue-view.views.issue-base.content.issue-links.issue-links-view.relationship-heading"
					>
						{name}
					</RelationshipHeading>
					{renderLineCardGroup(issues, name)}
				</GroupContainer>
			);
		},
		[renderIssueLinkInlineMessage, renderLineCardGroup],
	);

	const renderHeading = useCallback(
		() => (
			<SectionHeading>
				<Label htmlFor={ISSUE_LINK_SEARCH}>
					<SectionHeadingTitle>
						<FormattedMessage id="issue.issue-links-heading" defaultMessage="Linked issues" />
					</SectionHeadingTitle>
				</Label>
				<SectionHeadingIcons>
					{canAddIssueLinks && hasLinks && <AddIssueLinkButton onClick={onIssueLinkAddClick} />}
				</SectionHeadingIcons>
			</SectionHeading>
		),
		[canAddIssueLinks, hasLinks, onIssueLinkAddClick],
	);

	const renderExistingLinks = useCallback(() => {
		const renderedGroups = (
			<div>
				{issueLinks &&
					Object.keys(issueLinks)
						.sort()
						.map((group) => renderGroup(group, issueLinks[group]))}
			</div>
		);
		return isDevopsFeatureDisabledInFedRamp() ? (
			renderedGroups
		) : (
			<AsyncDevOpsSummaryContainer
				issueIds={getIssueIdsForDevSummaries(issueLinks ?? {})}
				location="issue_view_linked_issues_list"
			>
				{renderedGroups}
			</AsyncDevOpsSummaryContainer>
		);
	}, [issueLinks, renderGroup]);

	const renderAddIssueLink = useCallback(
		() => (
			<AddContainer>
				<div>
					<IssueLinksAddView
						issueLinkTypes={issueLinkTypes}
						baseUrl={baseUrl}
						issueKey={issueKey}
						onClose={onIssueLinkAddClosed}
						issueLinks={issueLinks}
						onAddSuccess={onSuccess}
						onAddError={onError}
					/>
				</div>
			</AddContainer>
		),
		[baseUrl, issueKey, issueLinkTypes, issueLinks, onError, onIssueLinkAddClosed, onSuccess],
	);

	const displayIssueLinks = hasLinks && !isEmpty(issueLinks);

	return (
		<UFOSegment name="issue-view-linked-issues">
			<JSErrorBoundary
				id="issue.issue-view.activity.linked-issues"
				packageName="jiraLinkedIssues"
				teamName="bento"
				fallback="unmount"
			>
				<ContextualAnalyticsData
					sourceName="linkedIssues"
					sourceType={SCREEN}
					attributes={{ location: 'jiraLinkedIssues' }}
				>
					{displayIssueLinks || displayAddIssueLinks ? renderHeading() : null}
					{displayIssueLinks ? renderExistingLinks() : null}
					{displayAddIssueLinks ? renderAddIssueLink() : null}
					{state.hasDeleteModal ? renderDeleteConfirmationModal() : null}
				</ContextualAnalyticsData>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

IssueLinksView.displayName = 'IssueLinksView';
IssueLinksViewOld.displayName = 'IssueLinksViewOld';

export default componentWithFF<Props, Props>(
	'analytics_event_for_deletion_of_linked_issue_1ufi8',
	IssueLinksView,
	IssueLinksViewOld,
);

const RelationshipHeading = (props: { 'data-testid': string; children: React.ReactNode }) => (
	<Heading as="h3" level="h200">
		<span data-testid={props['data-testid']}>{props.children}</span>
	</Heading>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupContainer = styled.div({
	margin: `${token('space.100', '8px')} 0`,
	paddingRight: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-child)': {
		paddingBottom: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddContainer = styled.div({
	margin: `${token('space.100', '8px')} 0`,
	paddingRight: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Label = styled.label({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	paddingBottom: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledSectionHeading = styled(SectionHeading)<{}>({
	paddingBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemLineCardGroupWrapper = styled.div({
	marginTop: token('space.100', '8px'),
});
